import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router';
import { NotesDialog } from '../../note/notes.dialog';
import { useSaveData } from '../../../hooks/useSaveData';
import { Customer } from '../../../models/customer';
import { customersApiKeyStore } from '../../../api/customers-api-key-store';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { FileUpload } from './file-upload/file-upload.component';
import { CustomerInfoForm } from './customer-info.form';
import { ContactAddressForm } from './contacts-address.form';
import { OtherFieldForm } from './other-field.form';
import { SetupButtons } from './setup-buttons.component';
import { FormButtons } from './form-buttons.component';
import { ContactsForm } from './contacts.form';
import { ServicesForm } from './services.form';
import { BillingDetailsForm } from './billing-details.form';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { ContractsForm } from './contracts.form';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Service } from '../../../models/masters/service';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { useWatch } from 'react-hook-form';
import { UsersRoleComponent } from './user-role-list.component';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';

const styling = {
  root: { paddingBottom: '10px' },
  buttonStyle: { width: '130px' },
};

interface SaveCustomerComponentProps {
  onEdit?: boolean;
  customer?: Customer;
}

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const customerValidationSchema = Yup.object().shape({
  customerName: Yup.string().required('Customer name is required'),
  customerTypes: Yup.array().required('Customer type is required'),
  // customerCode: Yup.string().required('Customer Code  is required'),
  customerPoRequirement: Yup.string().required('PO requirement is required'),
  // invoiceTermID: Yup.string().required('Invoice due days is required'),
  customerDivisions: Yup.array().required('Customer division is required'),
  zipcode: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable(),
  contactPhone: Yup.string()
    .matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' })
    .required('Contact phone number is required'),
  contactEmail: Yup.string()
    .nullable() // allows the field to be null or undefined
    .matches(emailRegex, { excludeEmptyString: true, message: 'Invalid email' })
    .required('Contact email is required'),
  billingEmail: Yup.string()
    .nullable() // allows the field to be null or undefined
    .matches(emailRegex, { excludeEmptyString: true, message: 'Invalid email' }),
  billingPhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' }),
  billingZip: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable(),
});

export const SaveCustomerComponent = ({ customer, onEdit }: SaveCustomerComponentProps) => {
  const navigate = useNavigate();
  const [noteTempEntityID, setNoteTempEntityID] = useState<number>();
  const [tempEntityID, setTempEntityID] = useState<any>('');
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const {
    isLoading,
    mutateAsync,
    data: customerData,
  } = useSaveData<Customer>(!onEdit ? customersApiKeyStore.createCustomers() : customersApiKeyStore?.updateCustomers());

  const { data } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceMappingList(!customer?.customerID ? 0 : customer?.customerID, 0, 0, 0, 0)
  );
  const form = useReactHookForm({
    defaultValues: {
      ...customer,
      state: { stateID: customer?.state ? customer?.state : 0, stateName: '' },
      billingState: customer?.billingState ? customer.billingState : 0,
      billingEmail: customer?.billingEmail ? customer.billingEmail : '',
      billingStreet: customer?.billingStreet ? customer.billingStreet : '',
      billingCity: customer?.billingCity ? customer.billingCity : '',
      syncWithDux: customer?.syncWithDux ? customer?.syncWithDux : false,

      // rebateCompanies: {
      //   utilRebateCompanyID: customer?.rebateCompanies ? customer?.rebateCompanies[0]?.utilRebateCompanyID ?? '' : '',
      //   utilRebateCompanyName: customer?.rebateCompanies
      //     ? customer?.rebateCompanies[0]?.utilRebateCompanyName ?? ''
      //     : '',updateCustomers
      // },
    },
    yupSchema: customerValidationSchema,
  });

  useEffect(() => {
    if (data && customer?.customerID) {
      data?.data?.map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          form.setValue('dryRunFee', service.price);
        } else if (service.serviceCode === 'Late Cancellation') {
          form.setValue('lateCancellationFee', service.price);
        } else if (service.serviceCode === 'Trip Charge') {
          form.setValue('tripCharge', service.price);
        }
      });
      // Toggle state to trigger re-render
    }
  }, [data]);

  const handleSubmit = async (customer: Customer) => {
    const filterData = data?.data
      ?.filter((d) => d.isNotReadyService)
      .map((service) => {
        if (service.serviceCode === 'Dry Run') {
          return {
            ...service,
            price: customer?.dryRunFee,
          };
        } else if (service.serviceCode === 'Late Cancellation') {
          return {
            ...service,
            price: customer?.lateCancellationFee,
          };
        } else if (service.serviceCode === 'Trip Charge') {
          return {
            ...service,
            price: customer?.tripCharge,
          };
        }
        return service; // Return the service unchanged if no condition matches
      });

    await mutateAsync({
      data: {
        ...customer,
        ...(noteTempEntityID && { noteTempEntityID }),
        ...(tempEntityID && { tempEntityID }),
        state: customer.state?.stateID,
        customerAccountManagers: customer.customerAccountManagers?.map((p) => p.userID),
        customerTypes: customer.customerTypes?.map((p) => p.customerTypeID),
        customerContacts: customer.customerContacts?.map((p) => p.contactsID),
        customerContactsList: customer.customerContacts?.map((p: any) => {
          return {
            contactsID: p.contactsID,
            syncWithDux: customer?.syncWithDux ? p.syncWithDux : false,
          };
        }),
        customerDivisions: customer.customerDivisions?.map((p) => p.divisionID),
        customerServices: [...(customer?.customerServices || []), ...(filterData || [])],
        rebateCompanies: null,
        status: 'Active',
      },
    }).then((data) => navigate(`/customers/details/${data?.data?.customerID}`));
  };

  const handleOpenNoteDialog = () => {
    setOpenNoteDialog(true);
  };
  const handleCloseNoteDialog = (tempNoteEntityID?: number) => {
    setOpenNoteDialog(false);
    if (tempNoteEntityID) {
      setNoteTempEntityID(tempNoteEntityID);
    }
  };

  const handleTempEntityID = (fileTempEntityID?: number) => {
    if (fileTempEntityID) {
      setTempEntityID(fileTempEntityID);
    }
  };

  const { showSnackbar } = useSnackbar();

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);
    const errorCount = messages?.length > 1 ? ` | +${messages.length - 1} more` : '';

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  // const handleFormSubmit = form.handleSubmit((values) => {
  //   console.log(values);
  //   handleSubmit(values as Customer);
  //   handleFormError(values as any);
  // });

  const handleFormSubmit = form.handleSubmit(handleSubmit as any, handleError);

  return (
    <div>
      <ReactHookForm formName="EDIT_CUSTOMER_FORM" onSubmit={() => {}} formContextProps={form}>
        <div>
          <Grid container spacing={1.5} style={styling.root}>
            <LoadingIndicator isLoading={isLoading} />
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <CustomerInfoForm isEdit={!customer?.customerID} customerId={customer?.customerID} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <OtherFieldForm />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ContactAddressForm customer={customer} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <BillingDetailsForm />
            </Grid>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <ContactsForm name="customerContacts" label="Contacts" />
              </Grid>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE_CUSTOMER]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                {/* {trigger?.length === 0 ? null : ( */}
                <ServicesForm
                  name="customerServices"
                  page="customer"
                  id={!customer?.customerID ? 0 : customer?.customerID}
                />
                {/* )} */}
              </Grid>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_FILES]}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FileUpload
                  fileRequestArgs={{ ...(customer ? { customerID: customer.customerID } : { tempEntityID }) }}
                  handleTempEntityID={handleTempEntityID}
                />
              </Grid>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTRACT_CUSTOMER]}>
              {onEdit && (
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                  <ContractsForm name="customerContracts" />
                </Grid>
              )}
            </GuardedComponent>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <UsersRoleComponent name="customerUsers" />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <SetupButtons openNoteDialog={handleOpenNoteDialog} />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FormButtons isLoading={isLoading} id={customer?.customerID} handleSubmit={handleFormSubmit} />
            </Grid>
          </Grid>
        </div>
      </ReactHookForm>
      <NotesDialog
        title="CUSTOMER"
        noteRequestArgs={{ ...(customer ? { customerID: customer.customerID } : { noteTempEntityID }) }}
        open={openNoteDialog}
        onClose={handleCloseNoteDialog}
      />
    </div>
  );
};
